import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Button, Modal, Form, Input, DatePicker, Dropdown } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import CountrySelector from "./CountrySelector";
import posthog from "posthog-js";
import { axiosBasic, getUserInfo } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import { Common } from "../../../../../utils";
import FetchingSpinner from "../../../FetchingSpinner";
import "../Settings.css";
import "./PublicHolidays.css";

const PublicHolidaysAPI = config.REST_API.PublicHolidays;

const PublicHolidays = () => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const [holidays, setHolidays] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    name: "",
    date: "",
    country: "",
    countryData: null,
    color: "#667eea",
    description: ""
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [availableCountriesCount, setAvailableCountriesCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableCountries, setAvailableCountries] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingHoliday, setDeletingHoliday] = useState(null);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidaysCount, setHolidaysCount] = useState(0);

  // Redirect to billing if subscription is suspended
  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  // Show restriction message if subscription is suspended
  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  const handleAddHoliday = async () => {
    // Validate inputs
    const errors = {};
    if (!newHoliday.name || newHoliday.name.trim() === "") {
      errors.name = "Holiday name is required";
    } else if (newHoliday.name.trim().length > 100) {
      errors.name = "Holiday name must be 100 characters or less";
    }
    if (!newHoliday.date) {
      errors.date = "Date is required";
    } else {
      // Check if date is in the past using string comparison to avoid timezone issues
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      // Compare date strings directly (YYYY-MM-DD format)
      if (newHoliday.date < todayStr) {
        errors.date = "Date cannot be in the past";
      }
    }
    // Check if country is selected
    // If selectedCountry exists, we should trust it (CountrySelector guarantees valid selections)
    // We'll reconstruct countryData if needed
    let countryDataToUse = newHoliday.countryData;

    // If selectedCountry is set but countryData is missing, try to reconstruct it
    if ((!countryDataToUse || !countryDataToUse.countryId) && selectedCountry && selectedCountry.trim() !== "") {
      // Try to find in availableCountries list
      let countryFromList = availableCountries.find(c => c.countryName === selectedCountry);

      // If not found in availableCountries but list exists, use first one (default selection)
      if (!countryFromList && availableCountries.length > 0) {
        countryFromList = availableCountries[0];
      }

      if (countryFromList && countryFromList.countryId) {
        countryDataToUse = {
          countryName: countryFromList.countryName,
          countryId: countryFromList.countryId,
          officeId: countryFromList.officeId,
          officeName: countryFromList.officeName,
          flag: countryFromList.flag
        };
        // Update newHoliday for this submission
        newHoliday.countryData = countryDataToUse;
        newHoliday.country = countryDataToUse.countryName;
      }
    }

    // Only show error if neither countryData nor selectedCountry exists
    if ((!countryDataToUse || !countryDataToUse.countryId) && (!selectedCountry || selectedCountry.trim() === "")) {
      errors.country = "Country is required";
    }

    // Validate description length
    if (newHoliday.description && newHoliday.description.length > 200) {
      errors.description = "Description must be 200 characters or less";
    }

    // Check if date already has a public holiday
    if (newHoliday.date && !errors.date) {
      const selectedDateStr = newHoliday.date;
      const existingHoliday = holidays.find(holiday => {
        // Normalize dates for comparison
        const holidayDateStr = holiday.date;

        // Check if dates match
        if (holidayDateStr === selectedDateStr) {
          // If country is selected, check if it's for the same country
          if (countryDataToUse?.countryId) {
            return holiday.countryId === countryDataToUse.countryId;
          }
          // If no country selected yet, check all holidays
          return true;
        }
        return false;
      });

      if (existingHoliday) {
        errors.date = `A public holiday "${existingHoliday.name}" already exists on this date${countryDataToUse?.countryName ? ` for ${countryDataToUse.countryName}` : ''}`;
      }
    }

    setValidationErrors(errors);

    // If there are errors, don't submit
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare API request body
      // Validate and convert date to ISO string for DateTime
      if (!newHoliday.date) {
        setValidationErrors({ ...validationErrors, date: "Date is required" });
        setIsSubmitting(false);
        return;
      }

      // Parse date properly - handle YYYY-MM-DD format from input
      const dateObj = new Date(newHoliday.date + 'T00:00:00'); // Add time to avoid timezone issues

      if (isNaN(dateObj.getTime())) {
        setValidationErrors({ ...validationErrors, date: "Invalid date format" });
        setIsSubmitting(false);
        return;
      }

      const dateTime = dateObj.toISOString();

      // Use the validated countryData (either from state or found during validation)
      const finalCountryData = countryDataToUse || newHoliday.countryData;

      const requestBody = {
        Name: newHoliday.name,
        DateTime: dateTime,
        Description: newHoliday.description || "",
        CountryId: finalCountryData?.countryId,
        Year: dateObj.getFullYear()
      };

      // Call API to add public holiday
      const response = await axiosBasic.post(
        PublicHolidaysAPI.AddPublicHolidayWithLeaves,
        requestBody
      );

      const success = response.data && response.data.success;
      const userId = getUserInfo()?.selfUserId ?? null;
      const userInput = {
        hasName: !!(newHoliday?.name?.trim()),
        hasDate: !!newHoliday?.date,
        countryId: finalCountryData?.countryId ?? null,
        year: dateObj.getFullYear(),
      };
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("addPublicHoliday", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to add public holiday"),
        });
      }

      // If API call successful, add the returned holiday to the existing list
      if (success) {
        // Get the returned holiday entity from the response
        const returnedHoliday = response.data.data;

        if (returnedHoliday) {
          // Safely parse the date from API response
          // Priority: 1) Use the date we submitted (most reliable), 2) Parse from API response
          let parsedDate = newHoliday.date; // Start with the date we submitted

          if (!parsedDate && returnedHoliday.publicHolidayDateTime) {
            try {
              // Parse the date and extract just the date part (YYYY-MM-DD) without timezone conversion
              const dateFromAPI = new Date(returnedHoliday.publicHolidayDateTime);
              if (!isNaN(dateFromAPI.getTime())) {
                // Use UTC methods to avoid timezone issues - this preserves the date as stored in DB
                const year = dateFromAPI.getUTCFullYear();
                const month = String(dateFromAPI.getUTCMonth() + 1).padStart(2, '0');
                const day = String(dateFromAPI.getUTCDate()).padStart(2, '0');
                parsedDate = `${year}-${month}-${day}`;
              } else {
                // If parsing fails, try to extract date from string directly
                const dateStr = returnedHoliday.publicHolidayDateTime.toString();
                const dateMatch = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
                if (dateMatch) {
                  parsedDate = dateMatch[1];
                }
              }
            } catch (e) {
              // Try to extract date from string directly
              try {
                const dateStr = returnedHoliday.publicHolidayDateTime.toString();
                const dateMatch = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
                if (dateMatch) {
                  parsedDate = dateMatch[1];
                }
              } catch (e2) {
                if (typeof posthog !== "undefined" && posthog.capture) {
                  posthog.capture("publicHolidaysParseDate", {
                    type: "exception",
                    success: false,
                    reason: e2?.message ?? "Error extracting date from string",
                  });
                }
              }
            }
          }

          // Final fallbacks
          if (!parsedDate) {
            if (returnedHoliday.date) {
              parsedDate = returnedHoliday.date;
            } else {
              parsedDate = new Date().toISOString().split('T')[0];
            }
          }

          // Map the returned entity to match the existing holiday format
          const newHolidayEntity = {
            id: returnedHoliday.id || Date.now(),
            name: returnedHoliday.publicHolidayName || returnedHoliday.name || "Unknown Holiday",
            date: parsedDate,
            country: returnedHoliday.officeName || returnedHoliday.country || "Unknown Office",
            countryId: returnedHoliday.countryId,
            officeId: returnedHoliday.officeId,
            officeName: returnedHoliday.officeName,
            description: returnedHoliday.publicHolidayDescription || returnedHoliday.description || "",
            isCustom: returnedHoliday.publicHolidayIsCustom || false,
            year: returnedHoliday.year || new Date().getFullYear(),
            type: "Public Holiday",
            color: generateRandomColor(),
            isActive: true
          };

          // Add the new holiday to the existing list; count stays distinct-by-date
          setHolidays(prevHolidays => {
            const next = [...prevHolidays, newHolidayEntity];
            setHolidaysCount(new Set(next.map(h => h.date)).size);
            return next;
          });
        }

        // Reset the form
        setNewHoliday({
          name: "",
          date: "",
          country: "",
          countryData: null,
          color: "#667eea",
          description: ""
        });
        setSelectedCountry("");
        setValidationErrors({});
        setShowAddModal(false);
      }
    } catch (error) {
      const userId = getUserInfo()?.selfUserId ?? null;
      const userInput = {
        hasName: !!(newHoliday?.name?.trim()),
        hasDate: !!newHoliday?.date,
        countryId: newHoliday?.countryData?.countryId ?? null,
      };
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("addPublicHoliday", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(
        error?.response?.data?.message || 'Failed to add public holiday. Please try again.',
        'Add Failed'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCountryChange = (countryData) => {
    // Handle case where countryData might be null (when clearing)
    if (!countryData || !countryData.countryName) {
      setNewHoliday({
        ...newHoliday,
        country: "",
        countryData: null
      });
      setSelectedCountry("");
      // Re-validate date if one is selected
      if (newHoliday.date) {
        validateDateForExistingHoliday(newHoliday.date, null);
      }
      return;
    }

    // Ensure countryData has all required fields
    const completeCountryData = {
      countryName: countryData.countryName,
      countryId: countryData.countryId,
      officeId: countryData.officeId,
      officeName: countryData.officeName,
      flag: countryData.flag
    };

    setNewHoliday({
      ...newHoliday,
      country: completeCountryData.countryName,
      countryData: completeCountryData
    });
    setSelectedCountry(completeCountryData.countryName);

    // Re-validate date if one is selected, now that country is known
    if (newHoliday.date) {
      validateDateForExistingHoliday(newHoliday.date, completeCountryData);
    }
  };

  // Helper function to validate if a date already has a holiday
  const validateDateForExistingHoliday = (dateStr, countryData) => {
    if (!dateStr) return;

    const existingHoliday = holidays.find(holiday => {
      // Normalize dates for comparison
      const holidayDateStr = holiday.date;

      // Check if dates match
      if (holidayDateStr === dateStr) {
        // If country is selected, check if it's for the same country
        if (countryData?.countryId) {
          return holiday.countryId === countryData.countryId;
        }
        // If no country selected yet, check all holidays
        return true;
      }
      return false;
    });

    if (existingHoliday) {
      setValidationErrors(prev => ({
        ...prev,
        date: `A public holiday "${existingHoliday.name}" already exists on this date${countryData?.countryName ? ` for ${countryData.countryName}` : ''}`
      }));
    } else {
      // Clear date error if date is valid
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.date && newErrors.date.includes('already exists')) {
          delete newErrors.date;
        }
        return newErrors;
      });
    }
  };

  const handleFilterCountryChange = (countryData) => {
    // Handle clearing the filter
    if (!countryData.countryName) {
      setFilterCountry('');
      setSelectedCountry('');
      // Fetch holidays for all countries (no filter)
      if (availableCountries.length > 0) {
        // You might want to fetch holidays for all countries or just show existing ones
        console.log('Filter cleared, showing all holidays');
      }
      return;
    }

    // Store the selected country name for display
    setFilterCountry(countryData.countryName);

    // Store the country ID for filtering holidays
    setSelectedCountry(countryData.countryName);

    // Fetch holidays for the selected country and year
    if (countryData.countryId) {
      fetchHolidaysFromAPI(countryData.countryId, selectedYear);
    }
  };

  const handleCountriesLoaded = (count, countriesData) => {
    setAvailableCountriesCount(count);
    setAvailableCountries(countriesData || []);
    // Do NOT fetch holidays here - CountrySelector already calls onCountryChange with the first country,
    // which triggers handleFilterCountryChange and a single fetchHolidaysFromAPI. Fetching here caused double API calls.
  };

  // Handle year change
  const handleYearChange = (event, { value }) => {
    setSelectedYear(value);

    // Fetch holidays for the selected year if we have a country selected
    if (availableCountries.length > 0) {
      const selectedCountryData = availableCountries.find(country =>
        country.value === filterCountry || country.value === selectedCountry
      );

      if (selectedCountryData) {
        fetchHolidaysFromAPI(selectedCountryData.countryId, value);
      }
    }
  };

  // Generate random color for holidays
  const generateRandomColor = () => {
    const colors = [
      "#ff6b6b", "#4ecdc4", "#45b7d1", "#f7b731", "#a55eea",
      "#26de81", "#fd79a8", "#fdcb6e", "#6c5ce7", "#a29bfe",
      "#74b9ff", "#0984e3", "#00b894", "#e17055", "#d63031"
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Fetch holidays from API
  const fetchHolidaysFromAPI = async (countryId, year) => {
    if (!countryId) return;
    console.log('Fetching holidays for:', { countryId, year });
    setHolidaysLoading(true);
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { countryId, year };

    try {
      const response = await axiosBasic.get(PublicHolidaysAPI.GetPublicHolidaysByCountryId, {
        params: { countryId, year }
      });

      console.log('API Response:', response.data);

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("getPublicHolidaysByCountryId", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to fetch holidays"),
        });
      }

      if (success) {
        const apiHolidays = response.data.data.map((item, index) => {
          // Safely parse date from API response
          let parsedDate = "";
          if (item.publicHolidayDateTime) {
            try {
              const dateFromAPI = new Date(item.publicHolidayDateTime);
              if (!isNaN(dateFromAPI.getTime())) {
                parsedDate = dateFromAPI.toISOString().split('T')[0];
              } else {
                parsedDate = item.date || new Date().toISOString().split('T')[0];
              }
            } catch (e) {
              if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("publicHolidaysParseDate", {
                  type: "exception",
                  success: false,
                  reason: e?.message ?? "Error parsing date from API",
                });
              }
              parsedDate = item.date || new Date().toISOString().split('T')[0];
            }
          } else if (item.date) {
            parsedDate = item.date;
          } else {
            parsedDate = new Date().toISOString().split('T')[0];
          }

          return {
            id: item.id || index,
            publicHolidayId: item.publicHolidayId,
            name: item.publicHolidayName || "Unknown Holiday",
            date: parsedDate,
            country: item.officeName || "Unknown Office",
            countryId: item.countryId,
            officeId: item.officeId,
            officeName: item.officeName,
            description: item.publicHolidayDescription || "",
            isCustom: item.publicHolidayIsCustom || false,
            year: item.year || year,
            type: "Public Holiday", // Add default type
            color: generateRandomColor(),
            isActive: true
          };
        });

        console.log('Processed holidays:', apiHolidays);
        setHolidays(apiHolidays);
        // Count distinct dates (same day can have multiple holidays)
        const distinctDateCount = new Set(apiHolidays.map(h => h.date)).size;
        setHolidaysCount(distinctDateCount);
      } else {
        console.log('API response not successful:', response.data);
        setHolidays([]);
        setHolidaysCount(0);
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("getPublicHolidaysByCountryId", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      setHolidays([]);
      setHolidaysCount(0);
    } finally {
      setHolidaysLoading(false);
    }
  };

  const toggleHolidayStatus = (id) => {
    setHolidays(holidays.map(holiday =>
      holiday.id === id ? { ...holiday, isActive: !holiday.isActive } : holiday
    ));
  };

  const deleteHoliday = (id) => {
    const holiday = holidays.find(h => h.id === id || h.publicHolidayId === id);
    if (holiday) {
      setDeletingHoliday(holiday);
      setIsDeleteModalOpen(true);
    } else {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("publicHolidaysDelete", {
          type: "exception",
          success: false,
          reason: "Holiday not found for deletion",
          holidayId: id,
        });
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingHoliday) return;

    const holidayId = deletingHoliday.publicHolidayId || deletingHoliday.id;
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { holidayId };

    try {
      // Call API to delete public holiday
      const response = await axiosBasic.delete(
        `${PublicHolidaysAPI.DeletePublicHolidayWithLeaves}/${holidayId}`
      );

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("deletePublicHoliday", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to delete public holiday"),
        });
      }

      // If API call successful, remove from the list; count stays distinct-by-date
      if (success) {
        setHolidays(prevHolidays => {
          const next = prevHolidays.filter(holiday =>
            (holiday.publicHolidayId || holiday.id) !== holidayId
          );
          setHolidaysCount(new Set(next.map(h => h.date)).size);
          return next;
        });
        Common.showSuccessMessage('Public holiday deleted successfully');
      } else {
        Common.showErrorMessage(
          response.data?.message || 'Failed to delete public holiday. Please try again.',
          'Delete Failed'
        );
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("deletePublicHoliday", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(
        error?.response?.data?.message || 'Failed to delete public holiday. Please try again.',
        'Delete Failed'
      );
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingHoliday(null);
    }
  };

  const getUpcomingHolidays = () => {
    const today = new Date();
    console.log('getUpcomingHolidays - Today:', today);

    let filteredHolidays = holidays.filter(holiday => {
      const holidayDate = safeParseDate(holiday.date);
      if (!holidayDate) return false;

      const isUpcoming = holidayDate >= today;
      const isActive = holiday.isActive;
      console.log(`Holiday: ${holiday.name}, Date: ${holiday.date}, IsUpcoming: ${isUpcoming}, IsActive: ${isActive}`);
      return isUpcoming && isActive;
    });

    console.log('Upcoming holidays before country filter:', filteredHolidays);

    // Apply country filter if set
    if (filterCountry && filterCountry !== '') {
      // Find the selected country data
      const selectedCountryData = availableCountries.find(country =>
        country.countryName === filterCountry
      );

      if (selectedCountryData) {
        // Filter holidays that belong to offices of the selected country
        filteredHolidays = filteredHolidays.filter(holiday =>
          holiday.countryId === selectedCountryData.countryId
        );
        console.log('Upcoming holidays after country filter:', filteredHolidays);
      }
    }

    // Make holidays distinct by name and date (to avoid duplicates from multiple offices)
    const distinctHolidays = filteredHolidays.reduce((acc, holiday) => {
      const key = `${holiday.name}-${holiday.date}`;
      if (!acc.find(h => `${h.name}-${h.date}` === key)) {
        acc.push(holiday);
      }
      return acc;
    }, []);

    const sorted = distinctHolidays.sort((a, b) => {
      const dateA = safeParseDate(a.date);
      const dateB = safeParseDate(b.date);
      if (!dateA || !dateB) return 0;
      return dateA - dateB;
    });
    console.log('Distinct upcoming holidays:', sorted);
    return sorted;
  };

  const getFilteredHolidays = () => {
    console.log('getFilteredHolidays called with:', {
      holidaysCount: holidays.length,
      filterCountry,
      holidays: holidays.slice(0, 2) // Show first 2 for debugging
    });

    // If no filter is set, return all holidays
    if (!filterCountry || filterCountry === '') {
      console.log('No filter applied, returning all holidays:', holidays);
      return holidays;
    }

    // Find all offices for the selected country
    const selectedCountryData = availableCountries.find(country =>
      country.countryName === filterCountry
    );

    if (selectedCountryData) {
      // Filter holidays that belong to offices of the selected country
      const filtered = holidays.filter(holiday =>
        holiday.countryId === selectedCountryData.countryId
      );
      console.log('Filtered holidays by country ID:', filtered);
      return filtered;
    }

    console.log('Country not found, returning all holidays:', holidays);
    return holidays;
  };

  // Get distinct holidays for display (to avoid duplicates from multiple offices)
  const getDistinctHolidays = () => {
    const filteredHolidays = getFilteredHolidays();

    // Make holidays distinct by name and date
    const distinctHolidays = filteredHolidays.reduce((acc, holiday) => {
      const key = `${holiday.name}-${holiday.date}`;
      if (!acc.find(h => `${h.name}-${h.date}` === key)) {
        acc.push(holiday);
      }
      return acc;
    }, []);

    console.log('Distinct holidays for display:', distinctHolidays);
    return distinctHolidays;
  };

  const getCountriesCount = () => [...new Set(holidays.map(h => h.country))].length;

  // Helper function to safely parse dates
  const safeParseDate = (dateString) => {
    if (!dateString) return null;

    // Handle YYYY-MM-DD format by adding time to avoid timezone issues
    let dateValue = dateString;
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      dateValue = dateString + 'T00:00:00';
    }

    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (dateString) => {
    const date = safeParseDate(dateString);
    if (!date) return "Invalid Date";

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const holidayDate = safeParseDate(dateString);
    if (!holidayDate) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    holidayDate.setHours(0, 0, 0, 0);

    const diffTime = holidayDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="public-holidays-container">
      {/* Header Section */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon">
            <Icon name="flag" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("app.publicHolidays.title")}</h3>
            <p className="section-description">
              {t("app.publicHolidays.description")}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="settings-page-content">
        <div className="content-card" style={{ position: "relative", minHeight: "400px" }}>
          {holidaysLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.85)",
                zIndex: 10,
                borderRadius: "8px",
              }}
            >
              <FetchingSpinner text={t("app.publicHolidays.loadingHolidays")} />
            </div>
          )}
          {/* Info Section */}
          <div className="info-section">
            <div className="info-icon">
              <Icon name="info circle" />
            </div>
            <div className="info-text">
              <h3>{t("app.publicHolidays.managingPublicHolidays")}</h3>
              <p>
                {t("app.publicHolidays.managingPublicHolidaysDescription")}
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}>
                <Icon name="calendar check" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{holidaysCount}</h3>
                <p className="stat-label">Total Holidays</p>
              </div>
            </div>


            <div className="stat-card">
              <div className="stat-icon" style={{ background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)" }}>
                <Icon name="globe" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{availableCountriesCount}</h3>
                <p className="stat-label">Countries</p>
              </div>
            </div>


            <div className="stat-card">
              <div className="stat-icon" style={{ background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" }}>
                <Icon name="clock" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{getCountriesCount()}</h3>
                <p className="stat-label">Holidays This Month</p>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="filter-section">
            <div className="filter-controls">
              <div className="filter-item">
                <label className="filter-label">
                  <Icon name="filter" />
                  Filter by Country
                </label>
                <CountrySelector
                  onCountryChange={handleFilterCountryChange}
                  selectedCountry={filterCountry}
                  placeholder="All Countries"
                  className="filter-dropdown"
                  onCountriesLoaded={handleCountriesLoaded}
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">
                  <Icon name="calendar" />
                  Year Selection
                </label>
                <div className="date-navigation">
                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-link p-0 me-2 nav-button"
                      onClick={() => handleYearChange(null, { value: selectedYear - 1 })}
                    >
                      <Icon name="chevron left" />
                    </button>
                    <div className="period-display">
                      <div className="period-year">{selectedYear}</div>
                      <div className="period-label">Current Year</div>
                    </div>
                    <button
                      className="btn btn-link p-0 ms-2 nav-button"
                      onClick={() => handleYearChange(null, { value: selectedYear + 1 })}
                    >
                      <Icon name="chevron right" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add New Button */}
          <div className="add-section">
            <button
              className="add-button"
              onClick={() => setShowAddModal(true)}
            >
              <Icon name="plus circle" />
              Add New Holiday
            </button>
          </div>

          {/* Upcoming Holidays Section */}
          {getUpcomingHolidays().length > 0 && (
            <div className="upcoming-holidays-section">
              <h3 className="section-title">
                <Icon name="clock outline" />
                Upcoming Holidays
              </h3>
              <div className="upcoming-holidays-grid">
                {getUpcomingHolidays().slice(0, 3).map(holiday => {
                  // Find all offices that observe this holiday
                  const observingOffices = holidays
                    .filter(h => h.name === holiday.name && h.date === holiday.date)
                    .map(h => h.officeName);

                  return (
                    <div key={`${holiday.name}-${holiday.date}`} className="upcoming-holiday-card">
                      <div className="holiday-date-badge" style={{ background: holiday.color }}>
                        <div className="date-day">
                          {(() => {
                            const date = safeParseDate(holiday.date);
                            return date ? date.getDate() : '?';
                          })()}
                        </div>
                        <div className="date-month">
                          {(() => {
                            const date = safeParseDate(holiday.date);
                            return date ? date.toLocaleDateString('en-US', { month: 'short' }) : '???';
                          })()}
                        </div>
                      </div>
                      <div className="holiday-info">
                        <h4 className="holiday-name">{holiday.name}</h4>
                        <p className="holiday-country">
                          {observingOffices.length > 1
                            ? `${observingOffices.length} offices`
                            : observingOffices[0]
                          }
                        </p>
                        <p className="days-until">
                          {getDaysUntil(holiday.date) === 0 ? 'Today' :
                            getDaysUntil(holiday.date) === 1 ? 'Tomorrow' :
                              `${getDaysUntil(holiday.date)} days away`}
                        </p>
                        {observingOffices.length > 1 && (
                          <div className="observing-offices" style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                            <Icon name="building" size="small" />
                            <span>{[...new Set(observingOffices)].join(', ')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All Holidays List */}
          <div className="holidays-list-section">
            <h3 className="section-title">
              <Icon name="list" />
              {t("app.publicHolidays.allPublicHolidays")}
            </h3>
            <div className="holidays-grid">
              {console.log('Rendering holidays:', getDistinctHolidays())}
              {getDistinctHolidays().length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <Icon name="calendar outline" size="large" />
                  <p>No holidays found</p>
                  <p>Total holidays in state: {holidays.length}</p>
                  <p>Filter country: {filterCountry || 'None'}</p>
                </div>
              ) : (
                getDistinctHolidays().map(holiday => {
                  // Find all offices that observe this holiday
                  const observingOffices = holidays
                    .filter(h => h.name === holiday.name && h.date === holiday.date)
                    .map(h => h.officeName);

                  return (
                    <div key={`${holiday.name}-${holiday.date}`} className="holiday-card">
                      <div className="holiday-header">
                        <div className="holiday-color-indicator" style={{ background: holiday.color }}></div>
                        <div className="holiday-main-info">
                          <h4 className="holiday-name">{holiday.name}</h4>
                          <p className="holiday-date">{formatDate(holiday.date)}</p>
                          <p className="holiday-country">
                            {observingOffices.length > 1
                              ? `${observingOffices.length} offices`
                              : observingOffices[0]
                            } • {holiday.type}
                          </p>
                          {observingOffices.length > 1 && (
                            <div className="observing-offices" style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                              <Icon name="building" size="small" />
                              <span>{[...new Set(observingOffices)].join(', ')}</span>
                            </div>
                          )}
                        </div>
                        <div className="holiday-actions">
                          <button
                            className="delete-holiday-btn"
                            onClick={() => deleteHoliday(holiday.publicHolidayId)}
                          >
                            <Icon name="trash alternate" />
                          </button>
                        </div>
                      </div>

                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Holiday Modal */}
      <Modal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setValidationErrors({});
        }}
        size="small"
        className="modern-modal"
      >
        <Modal.Header>
          <Icon name="plus circle" />
          <span>Add New Public Holiday</span>
        </Modal.Header>
        <Modal.Content style={{ padding: "24px" }}>
          <Form>
            <Form.Field required error={!!validationErrors.name}>
              <label style={{ fontWeight: 600, color: "#2d3748", marginBottom: "8px" }}>
                Holiday Name
              </label>
              <Input
                placeholder="Enter holiday name"
                value={newHoliday.name}
                maxLength={100}
                onChange={(e, { value }) => {
                  // Limit input to maxLength
                  const trimmedValue = value.length > 100 ? value.substring(0, 100) : value;
                  setNewHoliday({ ...newHoliday, name: trimmedValue });

                  // Clear error if valid
                  if (validationErrors.name && trimmedValue.trim().length > 0 && trimmedValue.trim().length <= 100) {
                    setValidationErrors({ ...validationErrors, name: "" });
                  }
                  // Show error if exceeds limit
                  else if (trimmedValue.trim().length > 100) {
                    setValidationErrors({ ...validationErrors, name: "Holiday name must be 100 characters or less" });
                  }
                }}
                style={{ marginBottom: validationErrors.name ? "4px" : "0" }}
              />
              <div style={{
                fontSize: "0.75rem",
                color: "#718096",
                marginTop: "4px",
                textAlign: "right"
              }}>
                {newHoliday.name.length}/100
              </div>
              {validationErrors.name && (
                <div style={{
                  color: "#e53e3e",
                  fontSize: "0.875rem",
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <Icon name="exclamation circle" />
                  {validationErrors.name}
                </div>
              )}
            </Form.Field>
            <Form.Field required error={!!validationErrors.date}>
              <label style={{ fontWeight: 600, color: "#2d3748", marginBottom: "8px" }}>
                Date
              </label>
              <Input
                type="date"
                value={newHoliday.date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e, { value }) => {
                  setNewHoliday({ ...newHoliday, date: value });

                  // Validate date on change
                  if (value) {
                    // Get today's date as YYYY-MM-DD string for accurate comparison
                    const today = new Date();
                    const todayStr = today.toISOString().split('T')[0];

                    // Compare date strings directly to avoid timezone issues
                    if (value < todayStr) {
                      setValidationErrors(prev => ({ ...prev, date: "Date cannot be in the past" }));
                      return;
                    }

                    // Clear past date error if date is valid
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      if (newErrors.date === "Date cannot be in the past") {
                        delete newErrors.date;
                      }
                      return newErrors;
                    });

                    // Check if date already has a public holiday
                    validateDateForExistingHoliday(value, newHoliday.countryData);
                  } else {
                    // Clear all date errors if no date selected
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.date;
                      return newErrors;
                    });
                  }
                }}
                style={{ marginBottom: validationErrors.date ? "4px" : "0" }}
              />
              {validationErrors.date && (
                <div style={{
                  color: "#e53e3e",
                  fontSize: "0.875rem",
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <Icon name="exclamation circle" />
                  {validationErrors.date}
                </div>
              )}
            </Form.Field>
            <Form.Field required error={!!validationErrors.country}>
              <label style={{ fontWeight: 600, color: "#2d3748", marginBottom: "8px" }}>
                Country
              </label>
              <CountrySelector
                onCountryChange={(countryData) => {
                  handleCountryChange(countryData);
                  if (validationErrors.country) {
                    setValidationErrors({ ...validationErrors, country: "" });
                  }
                }}
                onCountriesLoaded={(count, countriesData) => {
                  // Update availableCountries when countries are loaded in the modal
                  setAvailableCountries(countriesData || []);
                }}
                selectedCountry={selectedCountry}
                placeholder="Select Country"
                className="form-country-selector"
              />
              {validationErrors.country && (
                <div style={{
                  color: "#e53e3e",
                  fontSize: "0.875rem",
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <Icon name="exclamation circle" />
                  {validationErrors.country}
                </div>
              )}
            </Form.Field>
            <Form.Field error={!!validationErrors.description}>
              <label style={{ fontWeight: 600, color: "#2d3748", marginBottom: "8px" }}>
                Description (Optional)
              </label>
              <Form.TextArea
                placeholder="Enter holiday description..."
                value={newHoliday.description}
                maxLength={200}
                onChange={(e, { value }) => {
                  // Limit input to maxLength
                  const trimmedValue = value.length > 200 ? value.substring(0, 200) : value;
                  setNewHoliday({ ...newHoliday, description: trimmedValue });

                  // Clear error if valid
                  if (validationErrors.description && trimmedValue.length <= 200) {
                    setValidationErrors({ ...validationErrors, description: "" });
                  }
                  // Show error if exceeds limit
                  else if (trimmedValue.length > 200) {
                    setValidationErrors({ ...validationErrors, description: "Description must be 200 characters or less" });
                  }
                }}
                rows={3}
                style={{
                  border: validationErrors.description ? "2px solid #e53e3e" : "2px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "12px",
                  fontSize: "1rem",
                  fontFamily: "inherit",
                  resize: "vertical",
                  minHeight: "80px",
                  marginBottom: validationErrors.description ? "4px" : "0"
                }}
              />
              {validationErrors.description && (
                <div style={{
                  color: "#e53e3e",
                  fontSize: "0.875rem",
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <Icon name="exclamation circle" />
                  {validationErrors.description}
                </div>
              )}
              <div style={{
                fontSize: "0.75rem",
                color: validationErrors.description ? "#e53e3e" : "#718096",
                marginTop: "4px",
                textAlign: "right"
              }}>
                {newHoliday.description ? newHoliday.description.length : 0}/200
              </div>
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions style={{
          background: "#f7fafc",
          borderTop: "1px solid #e2e8f0",
          borderRadius: "0 0 12px 12px",
          padding: "16px 24px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px"
        }}>
          <Button
            onClick={() => {
              setShowAddModal(false);
              setValidationErrors({});
            }}
            style={{
              background: "#e2e8f0",
              color: "#4a5568",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
          >
            Cancel
          </Button>
          <Button
            primary
            onClick={handleAddHoliday}
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{
              background: isSubmitting ? "#cbd5e0" : "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              boxShadow: isSubmitting ? "none" : "0 2px 8px rgba(250, 112, 154, 0.3)"
            }}
          >
            {isSubmitting ? 'Adding...' : 'Add Holiday'}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingHoliday(null);
        }}
        size="tiny"
        className="delete-confirm-modal"
      >
        <Modal.Header style={{
          background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
          color: "white",
          borderRadius: "12px 12px 0 0",
          padding: "24px 28px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          boxShadow: "0 4px 12px rgba(255, 107, 107, 0.3)"
        }}>
          <Icon name="trash alternate" size="large" />
          Delete Public Holiday
        </Modal.Header>
        <Modal.Content style={{ padding: "24px 28px" }}>
          <div style={{ fontSize: "15px", lineHeight: "1.6" }}>
            <p style={{ marginBottom: "16px", color: "#2d3748" }}>
              Are you sure you want to delete <strong style={{ color: "#1a202c" }}>"{deletingHoliday?.name || 'this public holiday'}"</strong>?
            </p>

          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderRadius: "0 0 12px 12px",
          padding: "16px 28px"
        }}>
          <Button
            onClick={() => {
              setIsDeleteModalOpen(false);
              setDeletingHoliday(null);
            }}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(102, 126, 234, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            style={{
              background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(255, 107, 107, 0.3)",
              marginLeft: "12px"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(255, 107, 107, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(255, 107, 107, 0.3)";
            }}
          >
            Delete
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default PublicHolidays;
